import React, { useState } from 'react';
import { portsOf } from '../data/agentosGraph';
import registry from '../data/entityRegistry.gen.json';

// Tests card on the nQube canvas right rail ([[nqube-pipeline-tests]]).
// A test = fixture + expectations graded on a DRY run (writers report what
// they would commit, nothing lands). The card lists the pipeline's tests with
// their last verdict, runs one (queues the same fan-out job as Run with
// spec.testId + dryRun), and points the canvas at the blocks a failure
// names. The verdicts themselves are server-written (pipeline.testResults).

const EXPECT_LABEL = {
  'run.done': 'run finishes',
  'block.port': (e) => `${e.blockId} leaves on ${e.port}`,
  'block.status': (e) => `${e.blockId} is ${e.status}`,
  'artifact.minBytes': (e) => `${e.blockId} writes ≥ ${e.min} bytes`,
  'artifact.contains': (e) => `${e.blockId} contains ${(e.text || []).map((t) => `"${t}"`).join(', ')}`,
  'budget.ms': (e) => `ends within ${Math.round(Number(e.max) / 1000)}s`,
};
const expectLabel = (e) => { const l = EXPECT_LABEL[e.kind]; return typeof l === 'function' ? l(e) : l || e.kind; };

const BLOCK_TYPES = registry.blockTypes || {};
const isWriter = (type) => /^output\./.test(String(type || '')) && type !== 'output.return';

// Mirrors backend defaultExpectations: the run finishes and every writer on
// the happy path (no gate, or a gate's first exit) receives data.
export function defaultExpectations(graph) {
  const expect = [{ kind: 'run.done' }];
  const blocks = graph?.blocks || [];
  const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
  for (const w of blocks.filter((b) => isWriter(b.type))) {
    const inbound = (graph.connections || []).filter((c) => c.to === w.id);
    const secondary = inbound.some((c) => {
      const from = byId[c.from];
      const ports = from ? portsOf(from, BLOCK_TYPES[from.type]) : null;
      return ports && c.fromPort && c.fromPort !== ports[0];
    });
    if (secondary) continue;
    expect.push({ kind: 'block.status', blockId: w.id, status: 'done' }, { kind: 'artifact.minBytes', blockId: w.id, min: 1 });
  }
  return expect;
}

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || 'test';

const VERDICT = { pass: ['is-ok', 'pass'], fail: ['is-danger', 'fail'], running: ['is-info', 'running'] };

export default function PipelineTestsCard({ pipeline, graph, onChangeTests, onRunTest, onFocus, disabled }) {
  const tests = Array.isArray(pipeline?.tests) ? pipeline.tests : [];
  const results = pipeline?.testResults || {};
  const last = pipeline?.lastTest || null;
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [datasetId, setDatasetId] = useState('');
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState('');

  const statusOf = (t) => (last?.testId === t.id && last.status === 'running' ? 'running' : results[t.id]?.status || null);
  const passed = tests.filter((t) => statusOf(t) === 'pass').length;
  const failed = tests.filter((t) => statusOf(t) === 'fail').length;

  const add = () => {
    const base = slug(name || 'Smoke test');
    const taken = new Set(tests.map((t) => t.id));
    let id = base;
    for (let n = 2; taken.has(id); n += 1) id = `${base}-${n}`;
    const test = { id, name: (name || 'Smoke test').slice(0, 80), fixture: datasetId ? { datasetId: datasetId.trim() } : {}, expect: defaultExpectations(graph), createdAt: Date.now() };
    onChangeTests([...tests, test]);
    setAdding(false); setName(''); setDatasetId('');
  };

  const run = async (t) => {
    setErr('');
    try { await onRunTest(t); } catch (e) { setErr(e.message); }
  };

  const note = tests.length
    ? `Tests card: ${tests.length} test${tests.length === 1 ? '' : 's'}, ${passed} passing, ${failed} failing${last ? `; latest "${last.testId}" ${last.status}` : ''}. ${tests.filter((t) => statusOf(t) === 'fail').map((t) => `"${t.name}" fails: ${(results[t.id]?.failures || []).map((f) => `${expectLabel(f.expect)} — ${f.got}`).join('; ')}`).join(' ').slice(0, 200)} A test is a dry run graded against expectations (nothing is committed). NoVA runs one with test_pipeline, adds one with add_pipeline_test.`
    : 'Tests card: no tests yet — "Add test" (a dry run graded against expectations: the run finishes and every writer receives data) or ask NoVA (add_pipeline_test / test_pipeline).';

  return (
    <div className="cs-card" style={{ padding: 12, marginBottom: 10 }} data-nova-note={note}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>Tests</strong>
        <span style={{ flex: 1 }} />
        {tests.length > 0 && (
          <span className="cs-help" style={{ fontSize: 11.5, margin: 0 }}>
            {passed} passing{failed ? ` · ${failed} failing` : ''}
          </span>
        )}
        {tests.length > 0 && <span className={`mf-chip ${failed ? 'is-danger' : passed === tests.length ? 'is-ok' : 'is-muted'}`} style={{ fontSize: 11 }}>{failed ? 'failing' : passed === tests.length ? 'passing' : 'untested'}</span>}
      </div>
      <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>
        Prove the design before it commits: a test runs the graph DRY on a fixture — writers report what they would write, a human gate auto-approves — and is graded against expectations. A failing latest test blocks NoVA's run_pipeline.
      </p>
      {tests.map((t) => {
        const st = statusOf(t);
        const row = results[t.id] || null;
        const [tone, label] = VERDICT[st] || ['is-muted', 'not run'];
        const failures = st === 'fail' ? row?.failures || [] : [];
        return (
          <div key={t.id} className="nq-test-row" style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" className="cs-linklike" style={{ fontSize: 12.5, textAlign: 'left', flex: 1, padding: 0 }} onClick={() => setOpen(open === t.id ? null : t.id)} title={`${t.expect.length} expectation${t.expect.length === 1 ? '' : 's'}`}>
                {t.name}
                <span className="cs-help" style={{ fontSize: 11, marginLeft: 6 }}>{t.fixture?.datasetId ? `on dataset ${t.fixture.datasetId.slice(0, 8)}…` : 'on the pipeline inputs'}</span>
              </button>
              <span className={`mf-chip ${tone}`} style={{ fontSize: 11 }}>{label}</span>
              <button type="button" className="cs-btn cs-btn-sm" onClick={() => run(t)} disabled={disabled || st === 'running'} data-nova-note={`Run test "${t.name}" dry`}>
                {st === 'running' ? 'Running…' : 'Run test'}
              </button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" title="Remove test" onClick={() => onChangeTests(tests.filter((x) => x.id !== t.id))} disabled={disabled}>×</button>
            </div>
            {open === t.id && (
              <ul className="cs-help" style={{ fontSize: 11.5, margin: '6px 0 0 14px', padding: 0 }}>
                {t.expect.map((e, i) => <li key={i}>{expectLabel(e)}</li>)}
              </ul>
            )}
            {failures.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {failures.map((f, i) => (
                  <button
                    key={i}
                    type="button"
                    className="cs-linklike"
                    style={{ display: 'block', fontSize: 11.5, color: 'var(--danger)', textAlign: 'left', padding: 0 }}
                    onClick={() => onFocus?.(f.blockId ? [f.blockId] : [])}
                    title="Point at the block on the canvas"
                  >
                    ✕ {expectLabel(f.expect)} — {f.got}
                  </button>
                ))}
                {row?.jobId && <span className="cs-help" style={{ fontSize: 11 }}>run {row.jobId}{row.ms ? ` · ${Math.round(row.ms / 1000)}s` : ''}</span>}
              </div>
            )}
            {st === 'pass' && row?.at && <span className="cs-help" style={{ fontSize: 11 }}>passed {new Date(row.at).toLocaleString()} · {row.checked} check{row.checked === 1 ? '' : 's'}</span>}
          </div>
        );
      })}
      {err && <p className="cs-help" style={{ fontSize: 12, marginTop: 6, color: 'var(--danger)' }}>{err}</p>}
      {adding ? (
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          <input className="cs-input" placeholder="Test name (e.g. Register from the sample contracts)" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="cs-input" placeholder="Fixture dataset id (optional — replaces every dataset input)" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} />
          <span className="cs-help" style={{ fontSize: 11 }}>Expectations: the run finishes and every writer on the happy path receives data. Ask NoVA for sharper ones (“the register must mention Acme”).</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={add}>Add</button>
            <button type="button" className="cs-btn cs-btn-sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="cs-btn cs-btn-sm" style={{ marginTop: 10 }} onClick={() => setAdding(true)} disabled={disabled}>Add test</button>
      )}
    </div>
  );
}
